import React, { useState, useEffect } from "react";
import { useQuery } from "react-query";
import { useInfiniteQuery } from "react-query";
import { Link, useSearchParams } from "react-router-dom";
import {
  Flame,
  MapPin,
  Users,
  Camera,
  Filter,
  RefreshCw,
  AlertTriangle,
  Clock,
  DownloadCloud,
} from "lucide-react";
import { apiService } from "../services/apiService";
import { useAuth } from "../contexts/AuthContext";
import LoadingSpinner from "../components/UI/LoadingSpinner";
import toast from "react-hot-toast";

const FiresPage = () => {
  const { isAdmin } = useAuth();
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    district: "",
    status: "",
    active_only: true,
    has_photos: false,
  });
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Initialize filters from URL parameters
  useEffect(() => {
    const statusParam = searchParams.get("status");
    const districtParam = searchParams.get("district");

    if (statusParam || districtParam) {
      setFilters((prev) => ({
        ...prev,
        status: statusParam || "",
        district: districtParam || "",
      }));
    }
  }, [searchParams]);

  // Fetch fires
  const {
    data: firesPages,
    isLoading,
    refetch,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery(
    ["fires", filters],
    ({ pageParam = 0 }) =>
      apiService.getFires({
        ...filters,
        limit: 50,
        offset: pageParam,
      }),
    {
      getNextPageParam: (lastPage) => {
        const nextOffset = lastPage.offset + lastPage.limit;
        return nextOffset < lastPage.total ? nextOffset : undefined;
      },
      refetchInterval: 30000,
    },
  );

  // Fetch districts for filter
  const { data: districtsData } = useQuery(
    ["districts", filters.active_only, filters.has_photos, filters.status],
    () =>
      apiService.getDistricts({
        active_only: filters.active_only,
        has_photos: filters.has_photos,
        status: filters.status,
      }),
  );

  useEffect(() => {
    if (
      filters.district &&
      districtsData?.districts &&
      !districtsData.districts.includes(filters.district)
    ) {
      setFilters((prev) => ({
        ...prev,
        district: "",
      }));
    }
  }, [districtsData, filters.district]);

  // Fetch statuses for filter
  const { data: statusesData } = useQuery(
    ["statuses", filters.active_only, filters.has_photos, filters.district],
    () =>
      apiService.getStatuses({
        active_only: filters.active_only,
        has_photos: filters.has_photos,
        district: filters.district,
      }),
  );

  useEffect(() => {
    if (
      filters.status &&
      statusesData?.statuses &&
      !statusesData.statuses.some((s) => s.status === filters.status)
    ) {
      setFilters((prev) => ({
        ...prev,
        status: "",
      }));
    }
  }, [statusesData, filters.status]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await refetch();
    setIsRefreshing(false);
    toast.success("Lista atualizada!");
  };

  const handleSync = async () => {
    if (!isAdmin()) return;

    try {
      setIsRefreshing(true);
      await apiService.syncFires();
      await refetch();
      toast.success("Sincronização concluída!");
    } catch (error) {
      toast.error("Erro na sincronização");
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleLoadMore = () => {
    fetchNextPage();
  };

  const getStatusBadge = (fire) => {
    if (fire.is_important) {
      return (
        <span className="status-badge status-important">
          <AlertTriangle className="w-3 h-3 mr-1" />
          Importante
        </span>
      );
    }
    if (fire.is_active) {
      return <span className="status-badge status-active">Ativo</span>;
    }
    return <span className="status-badge status-inactive">Inativo</span>;
  };

  const FireCard = ({ fire }) => (
    <Link
      to={`/fires/${fire.id}`}
      className="card p-6 hover:shadow-lg transition-shadow duration-200"
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-fire-500 rounded-full flex items-center justify-center">
            <Flame className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">
              {fire.location}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {fire.district}
              {fire.concelho && ` • ${fire.concelho}`}
            </p>
          </div>
        </div>
        {getStatusBadge(fire)}
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
          <MapPin className="w-4 h-4" />
          <span>{fire.natureza || "N/A"}</span>
        </div>
        <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
          <Camera className="w-4 h-4" />
          <span>{fire.photo_count} fotos</span>
        </div>
        <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
          <Users className="w-4 h-4" />
          <span>{fire.personnel} operacionais</span>
        </div>
        <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
          <Clock className="w-4 h-4" />
          <span>
            {fire.date_str} {fire.hour_str}
          </span>
        </div>
      </div>

      {fire.aerial_means > 0 && (
        <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
          <span>Meios aéreos: {fire.aerial_means}</span>
          <span>Veículos: {fire.terrain_vehicles}</span>
        </div>
      )}
    </Link>
  );

  const displayedFires =
    firesPages?.pages?.flatMap((page) => page.fires || []) || [];

  const totalFires = firesPages?.pages?.[0]?.total || 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Incêndios
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Lista de incêndios florestais em Portugal
          </p>
        </div>
        <div className="flex items-center space-x-3 mt-4 sm:mt-0">
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className="btn-secondary flex items-center space-x-2"
          >
            <RefreshCw
              className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`}
            />
            <span>Atualizar</span>
          </button>
          {isAdmin() && (
            <button
              onClick={handleSync}
              disabled={isRefreshing}
              className="btn-primary flex items-center space-x-2"
            >
              <DownloadCloud
                className={`w-4 h-4 ${isRefreshing ? "animate-spin" : ""}`}
              />
              <span>Sincronizar</span>
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="card p-4">
        <div className="flex items-center space-x-4">
          <Filter className="w-5 h-5 text-gray-500" />
          <div className="flex flex-wrap items-center gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Distrito
              </label>
              <select
                value={filters.district}
                onChange={(e) =>
                  setFilters({ ...filters, district: e.target.value })
                }
                className="form-input w-48"
              >
                <option value="">Todos os distritos</option>
                {districtsData?.districts?.map((district) => (
                  <option key={district} value={district}>
                    {district}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Estado
              </label>
              <select
                value={filters.status}
                onChange={(e) =>
                  setFilters({ ...filters, status: e.target.value })
                }
                className="form-input w-48"
              >
                <option value="">Todos os estados</option>
                {statusesData?.statuses?.map((status) => (
                  <option key={status.status} value={status.status}>
                    {status.status} ({status.count})
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="active-only"
                checked={filters.active_only}
                onChange={(e) =>
                  setFilters({ ...filters, active_only: e.target.checked })
                }
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <label
                htmlFor="active-only"
                className="text-sm text-gray-700 dark:text-gray-300"
              >
                Apenas ativos
              </label>
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="has-photos"
                checked={filters.has_photos}
                onChange={(e) =>
                  setFilters({ ...filters, has_photos: e.target.checked })
                }
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <label
                htmlFor="has-photos"
                className="text-sm text-gray-700 dark:text-gray-300"
              >
                Apenas com fotos
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      {firesPages && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="card p-4 text-center">
            <div className="text-2xl font-bold text-fire-500">{totalFires}</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Total de incêndios
            </div>
          </div>
          <div className="card p-4 text-center">
            <div className="text-2xl font-bold text-primary-500">
              {displayedFires.filter((f) => f.is_active).length || 0}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Ativos
            </div>
          </div>
          <div className="card p-4 text-center">
            <div className="text-2xl font-bold text-yellow-500">
              {displayedFires.filter((f) => f.is_important).length || 0}
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Importantes
            </div>
          </div>
        </div>
      )}

      {/* Fires List */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : displayedFires.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayedFires.map((fire) => (
            <FireCard key={fire.id} fire={fire} />
          ))}
        </div>
      ) : (
        <div className="card p-12 text-center">
          <Flame className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            Nenhum incêndio encontrado
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            {filters.district ||
            filters.status ||
            filters.has_photos ||
            !filters.active_only
              ? "Tente ajustar os filtros para ver mais resultados."
              : "Não há incêndios ativos no momento."}
          </p>
        </div>
      )}

      {/* Load more button if there are more results */}
      {hasNextPage && (
        <div className="text-center">
          <button
            className="btn-secondary"
            onClick={handleLoadMore}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? "A carregar..." : "Carregar mais incêndios"}
          </button>
        </div>
      )}
    </div>
  );
};

export default FiresPage;
